import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import { FiCheckCircle, FiFileText } from "react-icons/fi";

import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table,
  TableToolbar,
  Image,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  ImageInsert,
  HorizontalLine,
} from "ckeditor5";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";

export default function ManageContents() {
  const navigate = useNavigate();
  const location = useLocation();

  // SIDEBAR & AUTH

  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState("Contents");
  const [user, setUser] = useState(null);

  // DATA
  const [pages, setPages] = useState([]);
  const [languages, setLanguages] = useState([]);

  // SELECTED PAGE / LANGUAGE
  const [pageId, setPageId] = useState(Number(location.state?.pageId || 1));
  const [langId, setLangId] = useState(Number(location.state?.langId || 1));

  // ENGLISH CONTENT
  const [englishTitle, setEnglishTitle] = useState("");
  const [englishContent, setEnglishContent] = useState("");

  // OTHER LANGUAGE CONTENT
  const [otherTitle, setOtherTitle] = useState("");
  const [otherContent, setOtherContent] = useState("");

  // LANGUAGE NAMES
  const [englishLanguageName, setEnglishLanguageName] = useState("English");
  const [selectedLanguageName, setSelectedLanguageName] = useState("English");

  // LOADING & SAVING
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // PAGE 28 = EMAIL PAGE
  const isEmailPage = Number(pageId) === 28;

  // AUTH EFFECT

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, [navigate]);

  // CKEDITOR CONFIGURATION

  const editorConfig = {
    licenseKey: "GPL",
    plugins: [
      Essentials,
      Paragraph,
      Heading,
      Bold,
      Italic,
      Underline,
      Font,
      Alignment,
      List,
      Link,
      BlockQuote,
      Table,
      TableToolbar,
      Image,
      ImageToolbar,
      ImageStyle,
      ImageResize,
      ImageInsert,
      HorizontalLine,
    ],
    toolbar: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "|",
      "link",
      "blockQuote",
      "|",
      "insertTable",
      "|",
      "insertImage",
      "|",
      "horizontalLine",
    ],
    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
        {
          model: "heading4",
          view: "h4",
          title: "Heading 4",
          class: "ck-heading_heading4",
        },
      ],
    },
    image: {
      toolbar: [
        "imageTextAlternative",
        "|",
        "imageStyle:inline",
        "imageStyle:block",
        "imageStyle:side",
        "|",
        "resizeImage",
      ],
    },
    table: {
      contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
    },
  };

  // LOAD DATA

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await API.get("/contents", {
        params: {
          page_id: pageId,
          lang_id: langId,
        },
      });

      if (!response.data.success) {
        toasts.error(response.data.message || "Failed to load content");
        return;
      }

      const data = response.data;

      setPages(data.pages || []);
      setLanguages(data.languages || []);
      setEnglishLanguageName(data.englishLanguageName || "English");
      setSelectedLanguageName(data.selectedLanguageName || "English");

      const english = data.englishContent || {};
      setEnglishTitle(english.cont_title || "");
      setEnglishContent(english.cont_details || "");

      const other = data.selectedLanguageContent || {};

      if (Number(langId) === 1) {
        setOtherTitle("");
        setOtherContent("");
      } else {
        setOtherTitle(other.cont_title || "");
        setOtherContent(other.cont_details || "");
      }
    } catch (error) {
      console.error("LOAD CONTENT ERROR:", error);
      toasts.error(error.response?.data?.message || "Failed to load content");
    } finally {
      setLoading(false);
    }
  }, [pageId, langId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // PAGE CHANGE

  const handlePageChange = (e) => {
    const newPageId = Number(e.target.value);
    setPageId(newPageId);

    setEnglishTitle("");
    setEnglishContent("");
    setOtherTitle("");
    setOtherContent("");

    navigate(location.pathname, {
      replace: true,
      state: {
        pageId: newPageId,
        langId: langId,
      },
    });
  };

  // LANGUAGE CHANGE

  const handleLanguageChange = (e) => {
    const newLangId = Number(e.target.value);
    setLangId(newLangId);

    setEnglishTitle("");
    setEnglishContent("");
    setOtherTitle("");
    setOtherContent("");

    navigate(location.pathname, {
      replace: true,
      state: {
        pageId: pageId,
        langId: newLangId,
      },
    });
  };

  // SUBMIT

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pageId) {
      toasts.warning("Please select a page");
      return;
    }

    if (!englishTitle.trim()) {
      toasts.warning("Please enter English title");
      return;
    }

    if (Number(langId) !== 1 && !otherTitle.trim()) {
      toasts.warning("Please enter translated title");
      return;
    }

    try {
      setSaving(true);

      const response = await API.post("/contents", {
        page_id: pageId,
        lang_id: langId,
        title: englishTitle,
        content: englishContent,
        other_title: Number(langId) !== 1 ? otherTitle : "",
        other_content: Number(langId) !== 1 ? otherContent : "",
      });

      if (response.data?.success) {
        toasts.success(response.data.message || "Content saved successfully");
        await loadData();
      } else {
        toasts.error(response.data?.message || "Unable to save content");
      }
    } catch (error) {
      console.error("Save content error:", error);
      toasts.error(error.response?.data?.message || "Unable to save content");
    } finally {
      setSaving(false);
    }
  };

  // CLEAR

  // const handleClear = () => {
  //   setEnglishTitle("");
  //   setEnglishContent("");
  //   setOtherTitle("");
  //   setOtherContent("");
  // };

  // RENDER

  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/* HEADER */}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div className="form-card-icon">
                <FiFileText size={24} />
              </div>
              <div>
                <h2 className="um-modal-title mb-0">Manage Contents</h2>
                <p className="form-card-sub mb-0">
                  (View and update page content and translations)
                </p>
              </div>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="user-form-card">
            {loading ? (
              <div className="text-center py-5">
                <span className="spinner" />
                <div className="mt-2 text-muted">Loading content...</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* SELECTION CONFIGURATION */}
                <div className="sec-div">
                  <span>Page & Language Configuration</span>
                </div>

                <div className="row g-4">
                  {/* PAGE NAME */}
                  <div className="col-xl-6 col-lg-6 col-md-6">
                    <label className="um-label">
                      Page Name <span className="text-danger">*</span>
                    </label>
                    <select
                      className="um-input"
                      value={pageId}
                      onChange={handlePageChange}
                      disabled={saving}
                    >
                      <option value={0}>-- Select Page --</option>
                      {pages.map((page) => (
                        <option key={page.page_id} value={page.page_id}>
                          {page.page_alias}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* LANGUAGE */}
                  <div className="col-xl-6 col-lg-6 col-md-6">
                    <label className="um-label">
                      Language <span className="text-danger">*</span>
                    </label>
                    <select
                      className="um-input"
                      value={langId}
                      onChange={handleLanguageChange}
                      disabled={saving}
                    >
                      {languages.map((language) => (
                        <option key={language.lang_id} value={language.lang_id}>
                          {language.lang_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ENGLISH CONTENT SECTION */}
                <div className="sec-div mt-4">
                  <span>Content Details In {englishLanguageName}</span>
                </div>

                <div className="row g-4">
                  {/* ENGLISH TITLE */}
                  <div className="col-12">
                    <label className="um-label">
                      {isEmailPage ? "Email Subject" : "Page Title"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="um-input"
                      maxLength={100}
                      value={englishTitle}
                      onChange={(e) => setEnglishTitle(e.target.value)}
                      placeholder={
                        isEmailPage ? "Enter email subject" : "Enter page title"
                      }
                      disabled={saving}
                    />
                  </div>

                  {/* EMAIL WARNING ALERTS */}
                  {isEmailPage && (
                    <div className="col-12">
                      <div className="alert alert-warning mb-2">
                        <strong>Note:</strong> The mentioned phrases in the mail
                        content will be replaced with actual values.
                        <br />
                        Please do not change these phrases while editing the
                        mail content.
                        <br />
                        For the € symbol enter <strong>[euro]</strong>.
                      </div>
                      <div className="alert alert-danger mb-0">
                        Try to avoid usage of special characters in the content.
                        <br />
                        [client_id] - Client Id
                        <br />
                        [client_email] - Client Email
                        <br />
                        [client_title] - Client's Title
                        <br />
                        [client_family_name] - Client's Family Name
                        <br />
                        [discount_details] - Modelwise Discount Details
                      </div>
                    </div>
                  )}

                  {/* ENGLISH CKEDITOR */}
                  <div className="col-12">
                    <label className="um-label">
                      Content Body ({englishLanguageName})
                    </label>
                    <div className="ckeditor-wrapper">
                      <CKEditor
                        key={`english-${pageId}-${langId}`}
                        editor={ClassicEditor}
                        config={editorConfig}
                        data={englishContent}
                        disabled={saving}
                        onChange={(event, editor) => {
                          setEnglishContent(editor.getData());
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* OTHER LANGUAGE CONTENT SECTION */}
                {Number(langId) !== 1 && (
                  <>
                    <div className="sec-div mt-4">
                      <span>Content Details In {selectedLanguageName}</span>
                    </div>

                    <div className="row g-4">
                      {/* OTHER TITLE */}
                      <div className="col-12">
                        <label className="um-label">
                          {isEmailPage ? "Email Subject" : "Page Title"}{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="um-input"
                          maxLength={100}
                          value={otherTitle}
                          onChange={(e) => setOtherTitle(e.target.value)}
                          placeholder={
                            isEmailPage
                              ? `Enter email subject in ${selectedLanguageName}`
                              : `Enter page title in ${selectedLanguageName}`
                          }
                          disabled={saving}
                        />
                      </div>

                      {/* OTHER EMAIL WARNING ALERTS */}
                      {isEmailPage && (
                        <div className="col-12">
                          <div className="alert alert-warning mb-2">
                            <strong>Note:</strong> Do not change the replacement
                            phrases while editing the mail content.
                            <br />
                            For the € symbol enter <strong>[euro]</strong>.
                          </div>
                          <div className="alert alert-danger mb-0">
                            Try to avoid usage of special characters in the
                            content.
                            <br />
                            [client_id] - Client Id
                            <br />
                            [client_email] - Client Email
                            <br />
                            [client_title] - Client's Title
                            <br />
                            [client_family_name] - Client's Family Name
                            <br />
                            [discount_details] - Modelwise Discount Details
                          </div>
                        </div>
                      )}

                      {/* OTHER CKEDITOR */}
                      <div className="col-12">
                        <label className="um-label">
                          Content Body ({selectedLanguageName})
                        </label>
                        <div className="ckeditor-wrapper">
                          <CKEditor
                            key={`other-${pageId}-${langId}`}
                            editor={ClassicEditor}
                            config={editorConfig}
                            data={otherContent}
                            disabled={saving}
                            onChange={(event, editor) => {
                              setOtherContent(editor.getData());
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* FORM FOOTER BUTTONS */}
                <div className="user-form-footer mt-4">
                  {/* <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={handleClear}
                    disabled={saving}
                  >
                    <FiX />
                    Clear
                  </button> */}

                  <button
                    type="submit"
                    className="um-btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle />
                        Save Content
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      
    </Layout>
  );
}
